<script setup lang="ts">
import { CssVarColors } from "@/shared/themes/CssVarColors.interface";
import { PageBlock } from "@/views/blocks/PageBlock.interface";
import { SplitWithPatternProps } from "./SplitWithPatternProps";
import { computed } from "vue";
import EditableElement from "@/views/page/EditableElement.vue";
import Section from "@/views/blocks/shared/section/layout/section/Section.vue";
import Heading from "@/views/blocks/shared/section/typography/heading/Heading.vue";
import Paragraph from "@/views/blocks/shared/section/typography/paragraph/Paragraph.vue";
import FormField from "../components/form-field/FormField.vue";
import FormMessages from "../components/form-messages/FormMessages.vue";
import SubmitButton from "../components/submit-button/SubmitButton.vue";
import PrivacyPolicy from "../components/privacy-policy/PrivacyPolicy.vue";
import { useContactForm } from "../common/useContactForm";
import SecondaryButton from "@/views/blocks/shared/section/buttons/secondary-button/SecondaryButton.vue";
import { ICON_API_BASE_URL } from "@/apps/page/services/page.service";
import Image from "@/views/blocks/shared/section/media/image/Image.vue";

const props = defineProps<{
    block: PageBlock<SplitWithPatternProps>;
    pageColors: CssVarColors;
    pageUuid: string;
}>();

const dataValues = computed(() => props.block.dataValues);
const styleValues = computed(() => props.block.styleValues);

const { agreed, formSubmitted, isSubmitting, showSuccessMessage, showErrorMessage, handleSubmit } =
    useContactForm({
        // hookUrl: dataValues.value.submitButton.data.hookUrl,
        privacyPolicyHidden: dataValues.value.privacyPolicy.isHidden,
        validationMessage: dataValues.value.privacyPolicy.data.validationMessage,
        successMessage: dataValues.value.submitButton.data.successMessage,
        errorMessage: dataValues.value.submitButton.data.errorMessage,
        pageUuid: props.pageUuid,
        sectionId: props.block.id,
    });
</script>

<template>
    <Section :id="block.webId" :backgroundColor="styleValues.background">
        <div class="mx-auto grid grid-cols-1 lg:grid-cols-2">
            <!-- Left Panel -->
            <div class="px-6 pt-24 pb-20 lg:py-32 lg:px-12 xl:px-16 pattern">
                <div class="max-w-xl mx-auto lg:ml-auto lg:mr-0">
                    <EditableElement
                        v-if="!dataValues.title.isHidden"
                        :blockId="block.id"
                        field-name="title"
                    >
                        <Heading
                            :text="dataValues.title.data.text"
                            :tag="dataValues.title.data.tag"
                            :textColor="styleValues.text"
                            class="text-4xl sm:text-5xl mb-6"
                        />
                    </EditableElement>
                    <EditableElement
                        v-if="!dataValues.description.isHidden"
                        :blockId="block.id"
                        field-name="description"
                    >
                        <Paragraph
                            :text="dataValues.description.data.text"
                            :textColor="styleValues.text"
                            class="text-lg/8 opacity-70"
                        />
                    </EditableElement>
                    <div class="flex flex-col items-start gap-3 mt-10 opacity-70">
                        <EditableElement
                            v-if="!dataValues.address.isHidden"
                            :blockId="block.id"
                            field-name="address"
                        >
                            <div
                                class="flex items-center gap-3 w-full"
                                :style="{ color: styleValues.text }"
                            >
                                <Image
                                    :src="`${ICON_API_BASE_URL}/maps_and_travel/map-01.svg`"
                                    alt="Address"
                                    class="w-5 h-5"
                                />
                                <Paragraph
                                    :text="dataValues.address.data.text"
                                    :textColor="styleValues.text"
                                />
                            </div>
                        </EditableElement>
                        <EditableElement
                            v-if="!dataValues.phone.isHidden"
                            :blockId="block.id"
                            field-name="phone"
                        >
                            <div
                                class="flex items-center gap-3 w-full"
                                :style="{ color: styleValues.text }"
                            >
                                <Image
                                    :src="`${ICON_API_BASE_URL}/communication/phone-call-01.svg`"
                                    alt="Phone"
                                    class="w-5 h-5"
                                />
                                <SecondaryButton
                                    :label="dataValues.phone.data.label"
                                    :textColor="styleValues.text"
                                    :href="dataValues.phone.data.href"
                                    :show-arrow="false"
                                />
                            </div>
                        </EditableElement>
                        <EditableElement
                            v-if="!dataValues.email.isHidden"
                            :blockId="block.id"
                            field-name="email"
                        >
                            <div
                                class="flex items-center gap-3 w-full"
                                :style="{ color: styleValues.text }"
                            >
                                <Image
                                    :src="`${ICON_API_BASE_URL}/communication/mail-01.svg`"
                                    alt="Email"
                                    class="w-5 h-5"
                                />
                                <SecondaryButton
                                    :label="dataValues.email.data.label"
                                    :textColor="styleValues.text"
                                    :href="dataValues.email.data.href"
                                    :show-arrow="false"
                                />
                            </div>
                        </EditableElement>
                    </div>
                </div>
            </div>

            <!-- Right Panel (Form) -->
            <div class="px-6 pb-24 pt-16 lg:px-12 lg:py-32 xl:px-16">
                <form class="max-w-xl mx-auto lg:mr-auto lg:ml-0" @submit="handleSubmit">
                    <div class="grid grid-cols-1 gap-x-8 gap-y-6 sm:grid-cols-2">
                        <EditableElement
                            v-for="field in dataValues.formFields.data.items"
                            :key="field.id"
                            :blockId="block.id"
                            :field-name="`formFields_${field.id}`"
                            :hideable="false"
                        >
                            <FormField
                                v-bind="field"
                                :label-color="styleValues.text"
                                :accent-color="styleValues.brand"
                                :border-color="styleValues.border"
                            />
                        </EditableElement>
                        <EditableElement
                            v-if="!dataValues.privacyPolicy.isHidden"
                            :blockId="block.id"
                            field-name="privacyPolicy"
                        >
                            <PrivacyPolicy
                                v-bind="dataValues.privacyPolicy.data"
                                :accent-color="styleValues.brand"
                                v-model:agreed="agreed"
                            />
                        </EditableElement>
                    </div>
                    <div class="mt-6 space-y-4">
                        <EditableElement
                            v-if="!dataValues.submitButton.isHidden"
                            :blockId="block.id"
                            field-name="submitButton"
                        >
                            <SubmitButton
                                :label="dataValues.submitButton.data.label"
                                :text-color="'#ffffff'"
                                :background-color="styleValues.brand"
                                :is-submitting="isSubmitting"
                            />
                        </EditableElement>
                    </div>
                    <FormMessages
                        :formSubmitted="formSubmitted"
                        :agreed="agreed"
                        :validationMessage="dataValues.privacyPolicy.data.validationMessage"
                        :successMessage="dataValues.submitButton.data.successMessage"
                        :errorMessage="dataValues.submitButton.data.errorMessage"
                        :showSuccessMessage="showSuccessMessage"
                        :showErrorMessage="showErrorMessage"
                    />
                </form>
            </div>
        </div>
    </Section>
</template>

<style scoped>
.pattern {
    background-color: v-bind("styleValues.background");
    background-image: url("data:image/svg+xml,%3Csvg width='500' height='500' viewBox='0 0 40 40' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M20 20.5V18H0v-2h20v-2H0v-2h20v-2H0V8h20V6H0V4h20V2H0V0h22v20h2V0h2v20h2V0h2v20h2V0h2v20h2V0h2v20h2v2H20v-1.5zM0 20h2v20H0V20zm4 0h2v20H4V20zm4 0h2v20H8V20zm4 0h2v20h-2V20zm4 0h2v20h-2V20zm4 4h20v2H20v-2zm0 4h20v2H20v-2zm0 4h20v2H20v-2zm0 4h20v2H20v-2z' fill='%239C92AC' fill-opacity='0.02' fill-rule='evenodd'/%3E%3C/svg%3E");
}
</style>
